import { useMemo } from 'react';
import { fetchTickets } from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import { isAbort } from '../../api/http';
import type { Id, TicketListItem } from '../../api/types';
import { useResource, type Resource } from '../../hooks/useResource';
import type { Filters } from '../../components/backlog/FilterBar';
import type { SectionPage } from './sprint-sections';

/**
 * 🔴 WHY A SECTION'S ROWS ARE ITS OWN REQUEST, AND NOT A CLIENT-SIDE GROUPING OF ONE PAGE.
 *
 * The obvious build is: fetch page 1 of the project (50 rows, `updated_at DESC`) and group it by
 * `sprint_id`. It renders instantly, it looks exactly like this screen, and every number on it is
 * WRONG — a header would read "v1.0.33 · 7 work items" because seven of that sprint's twenty-six
 * happened to be among the fifty most recently touched tickets. ⭐⭐ That is the defect shape this
 * repo has already shipped once (the fixture that was kinder than the API): a screen that looks
 * finished while answering a different question.
 *
 * ⇒ `GET /tickets?projectId=&sprintId=` per section, so `total` is the server's own `count(*)` under
 *   the same filters the reader can see.
 *
 * ⭐⭐ AND ONLY FOR THE SECTIONS THAT ARE OPEN. `GET /projects/:id/sprints` already carries
 *   `ticket_count` and `story_points_total` per sprint, so a collapsed header needs no request at
 *   all — see `sprintSectionTotals`. A project with fourteen sprints therefore costs ONE ticket
 *   request on load, not fourteen. ⛔ Fetching every section up front is the client-side N+1 the
 *   API's own list query goes out of its way to avoid.
 */

/** The API's MAX_PAGE_SIZE. Asking for more is a 400, not a bigger page. */
export const SECTION_PAGE_SIZE = 100;

export interface SprintSectionIndex {
  index: ReadonlyMap<Id, SectionPage>;
  /** Sprints whose tickets could not be loaded. ⛔ Their sections must SAY so, not render empty. */
  failed: { sprintId: Id; reason: string }[];
}

const EMPTY_INDEX: SprintSectionIndex = { index: new Map(), failed: [] };

/**
 * The rows for every OPEN sprint section, fetched in parallel.
 *
 * ⚠️ `Promise.allSettled`, not `Promise.all` — the same rule as `useSubtaskIndex`. One sprint's
 * request failing must not blank the other open sections; it is reported against that section alone.
 */
export function useSprintSectionIndex(
  projectId: Id | null,
  openSprintIds: readonly Id[],
  filters: Filters,
): Resource<SprintSectionIndex> {
  // A stable primitive key: `deps` is compared by identity and a fresh array every render would
  // re-fetch forever. (Same trick, same reason, as `useSubtaskIndex`.)
  const key = useMemo(() => [...openSprintIds].sort().join(','), [openSprintIds]);
  const enabled = Boolean(projectId) && openSprintIds.length > 0;

  return useResource<SprintSectionIndex>(
    async (signal) => {
      if (!projectId || key === '') return EMPTY_INDEX;
      const ids = key.split(',');

      const settled = await Promise.allSettled(
        ids.map((sprintId) =>
          fetchTickets(
            {
              projectId,
              sprintId,
              status: filters.status,
              assignee: filters.assignee,
              page: 1,
              per_page: SECTION_PAGE_SIZE,
              // ⭐ The BACKLOG ordering, not `updated`. This screen is for planning the order of the
              //   work; `updated_at DESC` would reshuffle a sprint every time somebody commented.
              sort: 'rank',
            },
            signal,
          ),
        ),
      );

      const index = new Map<Id, SectionPage>();
      const failed: SprintSectionIndex['failed'] = [];

      settled.forEach((outcome, position) => {
        const sprintId = ids[position];
        if (sprintId === undefined) return;
        if (outcome.status === 'fulfilled') {
          index.set(sprintId, { items: outcome.value.items, total: outcome.value.total });
          return;
        }
        if (isAbort(outcome.reason)) return;
        const error = toApiError(outcome.reason, '/tickets?sprintId');
        failed.push({ sprintId, reason: error.reason ?? error.message });
      });

      return { index, failed };
    },
    [projectId, key, filters.status, filters.assignee],
    { enabled },
  );
}

export interface UnassignedScan {
  /** The tickets with no sprint, in the order the server returned them. */
  items: TicketListItem[];
  /**
   * The server's count of tickets with no sprint under the current filters.
   *
   * ⭐ EXACT now, and the same number `items` is drawn from. It used to be "how many rows we have
   *   looked at", a denominator for a scan that no longer happens.
   */
  total: number;
  /**
   * Whether the section holds every unassigned ticket, or only the first page of them.
   *
   * ⚠️ STILL A REAL CASE, and the reason this flag did not disappear with the scan: the API caps a
   *   page at 100. A project with 140 unassigned tickets gets 100 rows and `total: 140`, and saying
   *   "140" above a list of 100 would be a different lie from the one just removed.
   */
  truncated: boolean;
}

/**
 * The tickets with no sprint — ONE request, asking the question directly.
 *
 * 🔴 WHAT THIS REPLACED, because the reasoning is worth keeping even though the constraint is gone.
 *   `GET /tickets` could not be asked for "no sprint": `listTickets` built its WHERE from
 *   `if (filters.sprintId) …`, and a falsy value added no condition, so `sprintId=` did not filter —
 *   it returned THE WHOLE PROJECT under a narrower question. ⇒ This section used to be assembled by
 *   SCANNING the project's own pages and keeping the null rows, which forced a floor count ("14+"),
 *   a "scanned 100 of 486" line and a Scan-more button.
 *
 * ✅ The API now has `noSprint=true` (`t.sprint_id IS NULL`), so the count is the server's own and
 *   the fetch is one page rather than N. The floor, the scan progress and the button are gone with
 *   the workaround that needed them — ⛔ a leftover "+" on an exact number would be the same
 *   dishonesty pointing the other way.
 *
 * ⚠️ WHAT DID NOT CHANGE: `truncated`. 100 is still the page cap, so a section holding more than that
 *   still says so. Removing the workaround is not permission to claim completeness we do not have.
 */
export function useUnassignedScan(projectId: Id | null, filters: Filters): Resource<UnassignedScan> {
  // ⚠️ A sprint filter and "the tickets with no sprint" are contradictory questions — and the API now
  //    answers the combination with a 400 rather than an empty page, so this guard is load-bearing
  //    rather than merely tidy. The section is not rendered in that case either.
  const enabled = Boolean(projectId) && !filters.sprintId;

  return useResource<UnassignedScan>(
    async (signal) => {
      if (!projectId) return { items: [], total: 0, truncated: false };

      const page = await fetchTickets(
        {
          projectId,
          status: filters.status,
          assignee: filters.assignee,
          noSprint: true,
          page: 1,
          per_page: SECTION_PAGE_SIZE,
          sort: 'rank',
        },
        signal,
      );

      return {
        items: page.items,
        total: page.total,
        truncated: page.items.length < page.total,
      };
    },
    [projectId, filters.status, filters.assignee],
    { enabled },
  );
}
